import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import heic2any from "heic2any";

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const AddAdministrationDetail = () => {
  const jwtToken = localStorage.getItem("jwt_token");
  const organisation_id = localStorage.getItem("organisation_id");
  const history = useHistory();

  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone_number: "",
    employee_id: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [selectedPermissionType, setSelectedPermissionType] = useState(null);
  const [checkboxList, setCheckboxList] = useState([]);
  const [rewardCheckboxList, setRewardCheckboxList] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());
  const addCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith('data:')) return url; 
    if (url.startsWith('blob:')) return url; 
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${imageKey}`;
  };

  useEffect(() => {
    const fetchPermissionTypes = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/people-permission`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();

        if (res.ok && Array.isArray(data?.data)) {
          const perms = data.data.map((perm) => ({
            ...perm,
            value: perm.id,
            label: perm.type,
          }));
  
          const filteredPerms = perms.filter(
            (item) => item.type !== "Owner"
          );
  
          setPermissionTypes(filteredPerms);

          const employeePerm = filteredPerms.find(
            (item) => item.type === "Employee"
          );
  
          if (employeePerm) {
            setSelectedPermissionType(employeePerm);
            onChangePermission(employeePerm);
          }
        } else {
          toast.error("Permission data is empty or malformed");
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        toast.error("Permission dropdown API error");
      }
    };

    fetchPermissionTypes();
  }, [jwtToken]);

  useEffect(() => {
    if (!userDetail.first_name) return;
  
    const autoPassword = `mynameis${userDetail.first_name.toLowerCase()}`;
  
    setUserDetail((prev) => {
      if (
        !prev.password ||
        prev.password.startsWith("mynameis")
      ) {
        return {
          ...prev,
          password: autoPassword,
        };
      }
      return prev;
    });
  }, [userDetail.first_name]);
  

useEffect(() => {
  if (!userDetail.email || !userDetail.email.includes("@")) return;

  const emailPrefix = userDetail.email.split("@")[0];

  setUserDetail((prev) => {
    if (
      !prev.employee_id ||
      prev.employee_id === prev.email?.split("@")[0]
    ) {
      return {
        ...prev,
        employee_id: emailPrefix,
      };
    }
    return prev;
  });
}, [userDetail.email]);

  

  const onChangeHandler = (e) => {
    setUserDetail({ ...userDetail, [e.target.id]: e.target.value });
  };

  const handleImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const onChangePermission = (permission) => {
    setSelectedPermissionType(permission);

    const updatedPermissions = permission.permissions_list.map(
      (perm, index) => ({
        name: perm,
        checked: !(
          (permission.type === "External Instructor" &&
            (index === 0 ||
              index === permission.permissions_list.length - 1)) ||
          (permission.type === "Employee" &&
            index === permission.permissions_list.length - 1)
        ),
      })
    );
    setCheckboxList(updatedPermissions);

    const rewardPermissions =
      permission.reward_permissions_list?.map((perm) => ({
        name: perm,
        checked: true,
      })) || [];
    setRewardCheckboxList(rewardPermissions);
  };

  const onCheckboxHandler = (e, index) => {
    const updated = [...checkboxList];
    updated[index].checked = e.target.checked;
    setCheckboxList(updated);
  };

  const onRewardCheckboxHandler = (e, index) => {
    const updated = [...rewardCheckboxList];
    updated[index].checked = e.target.checked;
    setRewardCheckboxList(updated);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const fileType = file.type;

    try {
      let finalFile = file;

      // Convert HEIC/HEIF to JPEG
      if (fileType === "image/heic" || fileType === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        finalFile = new File([convertedBlob], file.name.replace(/\.[^/.]+$/, ".jpeg"), {
          type: "image/jpeg",
        });
      }

      setSelectedImage(finalFile);
      setImagePreviewUrl(URL.createObjectURL(finalFile));
      setImageKey(Date.now());
    } catch (error) {
      console.error("Image handling failed:", error);
      toast.error("Unsupported image format or conversion failed.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageKey(Date.now());
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    if (!userDetail.password) return toast.error("Password is required.");
    if (!selectedPermissionType)
      return toast.error("Please select an account type.");

    let base64Image = null;
    if (selectedImage) {
      try {
        base64Image = await handleImageToBase64(selectedImage);
      } catch (err) {
        return toast.error("Image conversion failed.");
      }
    }

    const payload = {
      ...userDetail,
      permission: {
        type: selectedPermissionType.type,
        checkbox: checkboxList
          .map((box, i) => (box.checked ? i : null))
          .filter((v) => v !== null)
          .join(","),
        reward_checkbox: rewardCheckboxList
          .map((box, i) => (box.checked ? i : null))
          .filter((v) => v !== null)
          .join(","),
      },
      organisation_id,
      profile_image: base64Image,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/add-user-to-organisation`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();

      if (res.ok) {
        if (result.data?.profile_image) {
          setImagePreviewUrl(result.data.profile_image);
          setImageKey(Date.now());
        }
        
        toast.success(result.message || "User created successfully");
        setTimeout(() => {
          history.push("/administration");
        }, 500);
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch {
      toast.error("Submission failed.");
    }
  };

  return (
    <form onSubmit={onSubmitHandler}>
      <h6>User Details</h6>
      <Box
        mt={2}
        mb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Profile Photo:
        </Typography>
        <input
          accept="image/png, image/jpeg, image/jpg, image/webp, image/heic, image/heif"
          type="file"
          id="upload-image"
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
        <label
          htmlFor="upload-image"
          style={{ position: "relative", display: "inline-block" }}
        >
          <CircleContainer>
            {imagePreviewUrl ? (
              <ImagePreview 
                key={imageKey}
                src={addCacheBuster(imagePreviewUrl)} 
                alt="Profile Preview" 
              />
            ) : (
              <span>Upload your profile photo.</span>
            )}
          </CircleContainer>

          {imagePreviewUrl && (
            <Button
              onClick={handleRemoveImage}
              sx={{
                minWidth: "unset",
                padding: 0,
                position: "absolute",
                top: "10px",
                right: "-10px",
                backgroundColor: "#f44336",
                color: "#fff",
                borderRadius: "50%",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
            >
              <RxCross2 size={16} />
            </Button>
          )}
        </label>
      </Box>

      <div className="row">
        {[
          "first_name",
          "last_name",
          "email",
          "password",
          "phone_number",
          "employee_id",
        ].map((field) => (
          <div className="col-md-6 mt-2" key={field}>
            <label htmlFor={field}>
              {field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              {field !== "employee_id" && field !== "phone_number" && (
                <span className="danger">*</span>
              )}
            </label>
            <div className="input-group">
              <input
                type={
                  field === "email"
                    ? "email"
                    : field === "phone_number"
                      ? "number"
                      : field === "password"
                        ? showPassword
                          ? "text"
                          : "password"
                        : "text"
                }
                className="form-control"
                id={field}
                required={field !== "employee_id" && field !== "phone_number"}
                value={userDetail[field]}
                onChange={onChangeHandler}
              />
              {field === "password" && (
                <span
                  className="input-group-text"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <h6 className="mt-3">User Permissions</h6>
      <div className="row">
        <div className="col-md-6 mt-2">
          <label>Account Type</label>
          <Select
            options={permissionTypes}
            value={selectedPermissionType}
            onChange={onChangePermission}
            placeholder="Select Account Type"
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-6 ">
          <label>People Risk Analysis Permissions</label>
          {checkboxList.length > 0 ? (
            checkboxList.map((perm, index) => (
              <div className="form-check" key={index}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`perm_${index}`}
                  checked={perm.checked}
                  onChange={(e) => onCheckboxHandler(e, index)}
                />
                <label className="form-check-label" htmlFor={`perm_${index}`}>
                  {perm.name
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </label>
              </div>
            ))
          ) : (
            <p className="text-muted">
              Select account type to view permissions
            </p>
          )}
        </div>

      </div>

      <button type="submit" className="btn mt-4 mb-3 bg_color">
        Add User
      </button>
    </form>
  );
};

export default AddAdministrationDetail;
